import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { Card, Button, Badge, Table } from '../components/ui.js';
import { Textarea, QueryState, ErrorState } from '../components/form.js';

interface QuestionRow {
  id: string;
  text: string;
  answered: boolean;
  answer: string | null;
  answeredAt: string | null;
  createdAt: string;
}
interface CheckInRow {
  id: string;
  date: string;
  maxTemperatureC: string | null;
  wetDiapers: number | null;
  stools: number | null;
  feedingType: string | null;
  sleepMood: string | null;
  symptoms: string[];
  note: string | null;
}
interface ObservationRow {
  id: string;
  date: string;
  text: string;
}

export function Observations() {
  const { t } = useI18n();

  const questionsQ = useQuery({ queryKey: ['questions'], queryFn: () => api.get<{ questions: QuestionRow[] }>('/questions') });
  const checkinsQ = useQuery({ queryKey: ['checkins'], queryFn: () => api.get<{ checkIns: CheckInRow[] }>('/checkins') });
  const obsQ = useQuery({ queryKey: ['observations'], queryFn: () => api.get<{ observations: ObservationRow[] }>('/observations') });

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-800 mb-4">{t('obs.title')}</h1>

      {/* --- Consultation questions (prominent running list) --- */}
      <Card title={t('weekly.questions')}>
        <QueryState
          isLoading={questionsQ.isLoading}
          isError={questionsQ.isError}
          error={questionsQ.error}
          errorFallback={t('common.error')}
          isEmpty={(questionsQ.data?.questions.length ?? 0) === 0}
          emptyMessage="—"
        >
          <ul className="space-y-3">
            {questionsQ.data?.questions.map((q) => (
              <QuestionItem key={q.id} q={q} />
            ))}
          </ul>
        </QueryState>
      </Card>

      {/* --- Daily check-ins --- */}
      <Card title={t('obs.dailyCheckins')}>
        <QueryState
          isLoading={checkinsQ.isLoading}
          isError={checkinsQ.isError}
          error={checkinsQ.error}
          errorFallback={t('common.error')}
          isEmpty={(checkinsQ.data?.checkIns.length ?? 0) === 0}
          emptyMessage="—"
        >
          <Table headers={[t('common.date'), t('checkin.temperature'), t('checkin.wetDiapers'), t('checkin.stools'), t('obs.symptoms')]}>
            {checkinsQ.data?.checkIns.map((c) => (
              <tr key={c.id} className="border-b border-slate-100 align-top">
                <td className="py-2 pr-4">{c.date}</td>
                <td className="py-2 pr-4">{c.maxTemperatureC ? `${c.maxTemperatureC} °C` : '—'}</td>
                <td className="py-2 pr-4">{c.wetDiapers ?? '—'}</td>
                <td className="py-2 pr-4">{c.stools ?? '—'}</td>
                <td className="py-2 pr-4">
                  {c.symptoms.length === 0 ? (
                    '—'
                  ) : (
                    <span className="flex flex-wrap gap-1">
                      {c.symptoms.map((s) => (
                        <Badge key={s} tone="warn">{t(`symptom.${s}`)}</Badge>
                      ))}
                    </span>
                  )}
                  {c.note && <div className="text-xs text-slate-400 mt-1">{c.note}</div>}
                </td>
              </tr>
            ))}
          </Table>
        </QueryState>
      </Card>

      {/* --- Free-text observations --- */}
      <Card title={t('weekly.observations')}>
        <QueryState
          isLoading={obsQ.isLoading}
          isError={obsQ.isError}
          error={obsQ.error}
          errorFallback={t('common.error')}
          isEmpty={(obsQ.data?.observations.length ?? 0) === 0}
          emptyMessage="—"
        >
          <ul className="space-y-2">
            {obsQ.data?.observations.map((o) => (
              <li key={o.id} className="text-sm border-b border-slate-100 pb-2">
                <span className="text-slate-400 mr-2">{o.date}</span>
                {o.text}
              </li>
            ))}
          </ul>
        </QueryState>
      </Card>
    </div>
  );
}

function QuestionItem({ q }: { q: QuestionRow }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [answering, setAnswering] = useState(false);
  const [answer, setAnswer] = useState('');

  const m = useMutation({
    mutationFn: () => api.post(`/questions/${q.id}/answer`, { answer: answer.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['questions'] });
      setAnswering(false);
    },
  });

  return (
    <li className="border border-slate-200 rounded-lg p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <p className="text-slate-800">{q.text}</p>
          <p className="text-xs text-slate-400 mt-0.5">{q.createdAt?.slice(0, 10)}</p>
          {q.answered && q.answer && (
            <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded px-2 py-1 mt-2">
              {q.answer}
            </p>
          )}
        </div>
        <div>
          {q.answered ? (
            <Badge tone="ok">✓</Badge>
          ) : (
            <Button variant="secondary" onClick={() => setAnswering((a) => !a)}>{t('common.save')}</Button>
          )}
        </div>
      </div>
      {answering && !q.answered && (
        <div className="mt-3">
          <Textarea rows={2} value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="…" />
          {m.isError && <div className="my-2"><ErrorState error={m.error} fallback={t('common.error')} /></div>}
          <div className="mt-2 flex gap-2">
            <Button onClick={() => m.mutate()} disabled={m.isPending || !answer.trim()}>{t('common.confirm')}</Button>
            <Button variant="secondary" onClick={() => setAnswering(false)}>{t('common.cancel')}</Button>
          </div>
        </div>
      )}
    </li>
  );
}
